import { makeAutoObservable, toJS } from 'mobx';
import { cloneDeep } from 'lodash';
import { tableColors } from '../../consts/colors';
import { getMaxArray } from './utils';
import { REPORT_TYPES } from '../../consts/reportTypes';

class Extraction {
  unplainedCategory = null;
  unplaiedFile = null;
  unplainedSheet = null;
  unplainedFunds = [];
  unplainedPickedFund = [];
  unplainedPickedChildFund = [];
  unplainedSelectedFund = [];
  selectedExtract = [];
  unplainedIsLockedPeriod = false;
  unplainedIsSidebarExpanded = false;
  state = {};
  history = [];
  currentIndex = 0;
  unplainedExtractionType = null;
  unplainedReportType = REPORT_TYPES.ANNEX_IV;

  constructor(initialState = {}) {
    makeAutoObservable(this);

    this.setFile = this.setFile.bind(this);
    this.setSheet = this.setSheet.bind(this);
    this.setFunds = this.setFunds.bind(this);
    this.setCategory = this.setCategory.bind(this);
    this.computeFunds = this.computeFunds.bind(this);
    this.setPickedFund = this.setPickedFund.bind(this);
    this.setSelectedFund = this.setSelectedFund.bind(this);
    this.setSelectedExtract = this.setSelectedExtract.bind(this);
    this.setPickedChildFund = this.setPickedChildFund.bind(this);
    this.setIsLockedPeriod = this.setIsLockedPeriod.bind(this);
    this.toggleExpandSidebar = this.toggleExpandSidebar.bind(this);
    this.setExtractionType = this.setExtractionType.bind(this);
    this.clear = this.clear.bind(this);
    this.set = this.set.bind(this);
    this.undo = this.undo.bind(this);
    this.redo = this.redo.bind(this);
    this.setInitialState = this.setInitialState.bind(this);
    this.state = { ...initialState };
    this.history = [this.state];
    this.currentIndex = 0;
    this.setReportType = this.setReportType.bind(this);
  }

  set(newState, addToHistory = true) {
    this.state = { ...newState };

    if (addToHistory) {
      this.history = [...this.history.slice(0, this.currentIndex + 1), this.state];
      this.currentIndex++;
    }
  }

  undo() {
    if (this.canUndo) {
      this.currentIndex--;
      this.state = { ...this.history[this.currentIndex] };
    }
  }

  redo() {
    if (this.canRedo) {
      this.currentIndex++;
      this.state = { ...this.history[this.currentIndex] };
    }
  }

  clear() {
    this.history = [this.state];
    this.currentIndex = 0;
  }

  setInitialState(newInitialState) {
    this.state = cloneDeep(newInitialState);
    this.history = [this.state];
    this.currentIndex = 0;
  }

  get canUndo() {
    return this.currentIndex > 0;
  }

  get canRedo() {
    return this.currentIndex < this.history.length - 1;
  }

  get reportType() {
    return toJS(this.unplainedReportType);
  }

  setReportType(_val) {
    this.unplainedReportType = _val;
  }

  setFile(_file) {
    this.unplaiedFile = _file;
  }

  setSheet(_sheet) {
    this.unplainedSheet = _sheet;
  }

  setSelectedExtract(value) {
    this.selectedExtract = value;
  }

  setFunds(_funds) {
    this.unplainedFunds = _funds ? _funds.slice() : [];
  }

  setIsLockedPeriod(_isLockedPeriod) {
    this.unplainedIsLockedPeriod = _isLockedPeriod;
  }

  setSelectedFund(_selectedFund) {
    this.unplainedSelectedFund = _selectedFund;
  }

  setCategory(_category) {
    this.unplainedCategory = _category;
  }

  setPickedFund(_pickedFund) {
    this.unplainedPickedFund = _pickedFund;
  }

  setPickedChildFund(_pickedChildFund) {
    this.unplainedPickedChildFund = _pickedChildFund;
  }

  toggleExpandSidebar(_isSidebarExpanded) {
    this.unplainedIsSidebarExpanded = _isSidebarExpanded;
  }

  setExtractionType(_extractionType) {
    this.unplainedExtractionType = _extractionType;
  }

  get funds() {
    return toJS(this.unplainedFunds);
  }

  get historyState() {
    return toJS(this.state);
  }

  get sheet() {
    return toJS(this.unplainedSheet);
  }

  get file() {
    return toJS(this.unplaiedFile);
  }

  get category() {
    return toJS(this.unplainedCategory);
  }

  get selectedFund() {
    return toJS(this.unplainedSelectedFund);
  }

  get pickedFund() {
    return toJS(this.unplainedPickedFund);
  }

  get pickedChildFund() {
    return toJS(this.unplainedPickedChildFund);
  }

  get isLockedPeriod() {
    return toJS(this.unplainedIsLockedPeriod);
  }

  get selectedExtractItems() {
    return toJS(this.selectedExtract);
  }

  get isSidebarExpanded() {
    return toJS(this.unplainedIsSidebarExpanded);
  }

  get extractionType() {
    return toJS(this.unplainedExtractionType);
  }

  computeFunds(categoryFromExtractedFile, fundMappings, extractedFile) {
    if (extractedFile) {
      let _funds = extractedFile?.funds?.map((item, index) => {
        const tables = categoryFromExtractedFile?.tables.map((value, j) => {
          const _columns = cloneDeep(value.columns);
          const records = item.data?.[value.name];
          // Build a per-record rowId fallback from sibling columns: cells in
          // the same record share a logical row. This lets us place
          // position-less cells (e.g. Fund name with no x/y) on the same
          // sidebar row as their positional siblings (Gross/Commitment rate).
          const rowIdByCellIndex = (records || []).map((_, i) => {
            for (const col of _columns) {
              const c = records?.[i]?.[col.name];
              if (c && c.y != null) return c.y + 1;
            }
            return i + 1;
          });
          _columns.forEach(val => {
            if (records) {
              val.values = records
                .map((cell, i) => {
                  const c = cell[val.name];
                  if (!c) return null;
                  // Cells returned without a position (e.g. "Fund name" with
                  // {v: text, x: null, y: null}) still carry text we need to
                  // display in the sidebar. Keep them with a same-record
                  // rowId and `columnId: null` so cell-highlighting (keyed
                  // by both) doesn't match them and create phantom marks.
                  if (c.x == null || c.y == null) {
                    if (c.v == null) return null;
                    return {
                      text: c.v,
                      rowId: rowIdByCellIndex[i],
                      columnId: null
                    };
                  }
                  return {
                    text: c.v ?? null,
                    rowId: c.y + 1,
                    columnId: c.x + 1
                  };
                })
                .filter(Boolean);
            }
          });
          return {
            ...value,
            columns: _columns,
            id: index + item.id + j
          };
        });

        const source_fund = fundMappings?.find(f => f.input_fund.name === item.name);

        return {
          ...item,
          ...tableColors[index],
          tables,
          id: item.id,
          name: item.name,
          data: null,
          source_fund_name: source_fund?.source_fund?.name
        };
      });

      if (!categoryFromExtractedFile?.is_fund) {
        _funds = categoryFromExtractedFile?.tables?.map((value, index) => {
          const _columns = cloneDeep(value.columns);
          const records = extractedFile?.non_funds?.data?.[value.name];
          const rowIdByCellIndex = (records || []).map((_, i) => {
            for (const col of _columns) {
              const c = records?.[i]?.[col.name];
              if (c && c.y != null) return c.y + 1;
            }
            return i + 1;
          });

          _columns.forEach(val => {
            if (records) {
              val.values = records
                .map((cell, i) => {
                  const c = cell[val.name];
                  if (!c) return null;
                  if (c.x == null || c.y == null) {
                    if (c.v == null) return null;
                    return {
                      text: c.v,
                      rowId: rowIdByCellIndex[i],
                      columnId: null
                    };
                  }
                  return {
                    text: c.v ?? null,
                    rowId: c.y + 1,
                    columnId: c.x + 1
                  };
                })
                .filter(Boolean);
            }
          });

          return {
            ...value,
            columns: _columns,
            id: value?.id || value.name + index + '-table',
            ...tableColors[index]
          };
        });
      }

      this.setCategory(categoryFromExtractedFile);
      this.setPickedFund(_funds?.map(item => item.id) || []);
      this.setSelectedFund(_funds?.map(item => item.id) || []);
      if (categoryFromExtractedFile?.is_fund) {
        this.setPickedChildFund(
          _funds
            ?.map(item => item.tables)
            ?.flat()
            ?.map(item => item.id)
        );
      }

      this.setFunds(_funds);

      this.setInitialState({
        funds: _funds
      });
    } else if (this.category) {
      let _funds = [];

      if (!categoryFromExtractedFile?.is_fund) {
        _funds = categoryFromExtractedFile?.tables?.map((item, index) => {
          return {
            ...item,
            ...tableColors[index],
            id: item?.id || item.name + index + '-table'
          };
        });
      } else {
        _funds = fundMappings
          ?.filter(val => !!val.source_fund)
          ?.map((item, index) => {
            const tables = categoryFromExtractedFile?.tables.map((value, j) => {
              return {
                ...value,
                id: item.input_fund.id + index + j
              };
            });

            return {
              ...item,
              tables,
              ...tableColors[index],
              id: item.input_fund.id,
              name: item.input_fund.name,
              source_fund_name: item?.source_fund?.name
            };
          });
      }

      this.setPickedFund([]);
      this.setSelectedFund([]);
      this.setPickedChildFund([]);
      this.setFunds(_funds);

      this.setInitialState({
        funds: _funds
      });
    } else {
      this.setInitialState({
        funds: []
      });
      this.setFunds([]);
      this.setPickedFund([]);
      this.setSelectedFund([]);
      this.setPickedChildFund([]);
    }
  }

  computeSubmitBody({
    funds,
    category,
    isLookup,
    header_idx,
    footer_idx,
    isStructured,
    isIndividualFund
  }) {
    let body = {};
    const lookupData = {};

    const _funds = funds.map(item => {
      const data = {};

      if (isLookup) {
        const rows = [];
        const _columns = getMaxArray(item.columns.map(val => val.values));
        item.columns.forEach(element => {
          if (element.values.length === 0) {
            _columns.forEach(cell => {
              const prevValue = rows[cell.rowId] || {};
              rows[cell.rowId] = {
                ...prevValue,
                [element.name]: {
                  v: null,
                  y: null,
                  x: null
                }
              };
            });
          } else {
            element.values.forEach(cell => {
              const prevValue = rows[cell.rowId] || {};
              rows[cell.rowId] = {
                ...prevValue,
                [element.name]: {
                  v: cell.text,
                  y: cell.rowId !== null ? cell.rowId - 1 : cell.rowId,
                  x: cell.columnId !== null ? cell.columnId - 1 : cell.columnId
                }
              };
            });
          }
        });
        let _filteredRows = [];

        Object.keys(rows).forEach(rowId => {
          if (rowId !== 'null') {
            _filteredRows[rowId] = rows[rowId];
            item.columns.forEach(({ name }) => {
              if (!_filteredRows[rowId][name]) {
                _filteredRows[rowId][name] = {
                  v: null,
                  x: null,
                  y: null
                };
              }
            });
          }
        });

        // Previously filtered rows outside the header/footer band when
        // `isStructured`. That silently dropped extracted cells whenever the
        // user moved the header or footer, so on reload the selection looked
        // smaller than what they had extracted. Keep every row that has any
        // value attached — let the band only affect rendering.
        lookupData[item.name] = Object.values(_filteredRows);
      } else {
        item.tables.forEach(value => {
          const rows = [];
          const _columns = getMaxArray(value.columns.map(val => val.values));
          value.columns.forEach(element => {
            if (element.values.length === 0) {
              _columns.forEach(cell => {
                const prevValue = rows[cell.rowId] || {};
                rows[cell.rowId] = {
                  ...prevValue,
                  [element.name]: {
                    v: null,
                    y: null,
                    x: null
                  }
                };
              });
            } else {
              element.values.forEach(cell => {
                const prevValue = rows[cell.rowId] || {};
                rows[cell.rowId] = {
                  ...prevValue,
                  [element.name]: {
                    v: cell.text,
                    y: cell.rowId !== null ? cell.rowId - 1 : cell.rowId,
                    x: cell.columnId !== null ? cell.columnId - 1 : cell.columnId
                  }
                };
              });
            }
          });

          let _filteredRows = [];

          Object.keys(rows).forEach(rowId => {
            if (rowId !== 'null') {
              _filteredRows[rowId] = rows[rowId];
              value.columns.forEach(({ name }) => {
                if (!_filteredRows[rowId][name]) {
                  _filteredRows[rowId][name] = {
                    v: null,
                    x: null,
                    y: null
                  };
                }
              });
            }
          });

          // See lookup branch above — header/footer band no longer trims the
          // saved selection.
          data[value.name] = Object.values(_filteredRows);
        });
      }

      return {
        id: item.id,
        name: item.name,
        tables: isLookup ? [] : item?.tables?.map(value => value.name),
        data
      };
    });

    if (isLookup) {
      body.non_funds = {
        tables: category?.tables?.map(value => value.name),
        data: lookupData
      };
    } else {
      body.funds = _funds;
    }

    if (isIndividualFund) {
      const fundName = body.funds ? body.funds[0]?.name : undefined;

      body.funds = body.funds?.map(fund => {
        const keys = Object.keys(fund.data);

        keys.forEach(_key => {
          fund.data[_key] = [...fund.data[_key]].map(row => {
            row['Fund name'] = {
              x: null,
              y: null,
              v: fundName
            };

            return row;
          });
        });

        return fund;
      });
    }

    return body;
  }
}

const extractionStore = new Extraction({
  funds: []
});

export default extractionStore;
